import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { PRACTICE_AREAS } from "../../data/content";

export default function PracticeDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const area = PRACTICE_AREAS.find((p) => p.slug === slug);
  const idx = PRACTICE_AREAS.findIndex((p) => p.slug === slug);
  const prev = idx > 0 ? PRACTICE_AREAS[idx - 1] : null;
  const next = idx < PRACTICE_AREAS.length - 1 ? PRACTICE_AREAS[idx + 1] : null;

  if (!area) {
    return (
      <div className="pt-40 pb-40 text-center" data-testid="page-practice-detail-notfound">
        <h1 className="text-4xl font-900 text-[#F2EFE9]">Practice area not found</h1>
        <Link to="/home/practice" className="mt-6 inline-block text-[#D95A41] hover:underline">
          Back to all practice areas
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`page-practice-${area.slug}`}>
      {/* Themed hero with practice-area background */}
      <section
        className="relative z-10 overflow-hidden min-h-[80vh] flex items-end"
        data-testid="detail-hero"
      >
        {/* Background image with painterly treatment */}
        <div className="absolute inset-0 z-0">
          <img
            src={area.bg}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover painterly-image"
          />
          {/* Watercolor paper / brush texture overlay via SVG turbulence */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
            <filter id={`brush-${area.slug}`}>
              <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="12" />
              <feColorMatrix values="0 0 0 0 0.95  0 0 0 0 0.92  0 0 0 0 0.85  0 0 0 0.5 0" />
            </filter>
            <rect
              width="100%"
              height="100%"
              filter={`url(#brush-${area.slug})`}
              opacity="0.7"
              style={{ mixBlendMode: "screen" }}
            />
          </svg>
          {/* Soft dark base for legibility (lighter than a photo overlay) */}
          <div className="absolute inset-0 bg-[#050A14]/40 mix-blend-multiply" />
          {/* Accent tint (watercolor wash) */}
          <div
            className="absolute inset-0 opacity-40"
            style={{ background: `radial-gradient(ellipse at 30% 40%, ${area.accent}B3, transparent 70%)` }}
          />
          {/* Bottom fade into the next section */}
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#050A14]" />
        </div>

        <div className="relative z-10 max-w-[1200px] w-full mx-auto px-6 lg:px-12 pt-32 pb-24">
          {/* Breadcrumb */}
          <Link
            to="/home/practice"
            className="inline-flex items-center gap-2 text-sm font-500 text-[#F2EFE9]/70 hover:text-[#F2EFE9] transition-colors mb-14 group"
            data-testid="detail-back-link"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            All Practice Areas
          </Link>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p
              className="text-sm font-500 tracking-[0.3em] uppercase mb-6"
              style={{ color: area.accent }}
            >
              {area.n} · Practice Area
            </p>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-900 tracking-tightest leading-[1] text-[#F2EFE9] max-w-4xl">
              {area.title}
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#F2EFE9]/80 leading-relaxed max-w-3xl">
              {area.desc}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="relative z-10 py-24 md:py-32 bg-[#050A14]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p
                className="text-sm font-500 tracking-[0.3em] uppercase"
                style={{ color: area.accent }}
              >
                Overview
              </p>
            </div>
            <div className="lg:col-span-8 space-y-6">
              {area.overview.map((para, i) => (
                <p
                  key={i}
                  className="text-lg text-[#F2EFE9]/85 leading-relaxed"
                  data-testid={`detail-overview-${i}`}
                >
                  {para}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="relative z-10 py-28 md:py-32 bg-[#050A14]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-4">
              <p className="text-sm font-500 tracking-[0.3em] uppercase text-[#D95A41] mb-4">
                What we cover
              </p>
              <h2 className="text-3xl md:text-5xl font-900 tracking-tightest leading-[1] text-[#F2EFE9]">
                Sub-services.
              </h2>
            </div>
            <div className="lg:col-span-8 lg:pt-4">
              <p className="text-lg text-[#94A3B8] leading-relaxed">
                A representative view of the work we take on within {area.title.toLowerCase()}. Every engagement is
                tailored to the specific commercial reality of the business.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 border-t border-white/10 pt-10">
            {area.services.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                className="flex gap-4"
                data-testid={`detail-service-${i}`}
              >
                <span className="mt-2 flex-shrink-0 w-7 h-7 rounded-full bg-[#D95A41]/15 border border-[#D95A41]/30 flex items-center justify-center">
                  <Check size={14} className="text-[#D95A41]" />
                </span>
                <div>
                  <h3 className="text-lg font-700 text-[#F2EFE9] tracking-tight mb-1.5">{s.title}</h3>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 py-24 md:py-28 bg-[#050A14]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="rounded-3xl border border-white/10 bg-[#0B132B]/60 px-8 md:px-12 py-12 md:py-14 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <p className="text-sm font-500 tracking-[0.3em] uppercase text-[#D95A41] mb-3">
                Discuss a mandate
              </p>
              <h3 className="text-2xl md:text-3xl font-700 text-[#F2EFE9] tracking-tight leading-tight">
                Bring us in early on your next {area.title.toLowerCase()} mandate.
              </h3>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/home/book")}
                data-testid="detail-cta-book"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D95A41] text-[#F2EFE9] font-700 hover:bg-[#E6735A] transition-colors whitespace-nowrap"
              >
                Book a consultation <ArrowRight size={16} />
              </button>
              <button
                onClick={() => navigate("/home/contact")}
                data-testid="detail-cta-contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-[#F2EFE9] font-500 hover:bg-white/5 transition-colors whitespace-nowrap"
              >
                Send an enquiry
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Prev / Next */}
      <section className="relative z-10 py-16 bg-[#050A14] border-t border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {prev ? (
            <Link
              to={`/home/practice/${prev.slug}`}
              className="group p-6 rounded-2xl border border-white/10 hover:border-[#D95A41]/50 transition-colors"
              data-testid="detail-prev"
            >
              <p className="text-xs font-500 tracking-[0.3em] uppercase text-[#94A3B8] flex items-center gap-2 mb-3">
                <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Previous
              </p>
              <h4 className="text-xl font-700 text-[#F2EFE9] tracking-tight">{prev.title}</h4>
            </Link>
          ) : <div />}
          {next ? (
            <Link
              to={`/home/practice/${next.slug}`}
              className="group p-6 rounded-2xl border border-white/10 hover:border-[#D95A41]/50 transition-colors md:text-right"
              data-testid="detail-next"
            >
              <p className="text-xs font-500 tracking-[0.3em] uppercase text-[#94A3B8] flex items-center gap-2 md:justify-end mb-3">
                Next <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </p>
              <h4 className="text-xl font-700 text-[#F2EFE9] tracking-tight">{next.title}</h4>
            </Link>
          ) : <div />}
        </div>
      </section>
    </div>
  );
}
